/**
 * Hook for managing the unit preference (gallons vs litres).
 *
 * Pi-backed (namespace `unit-preference`, value `'gal'` or `'L'`) via
 * `useServerBackedState`. The legacy localStorage copy
 * (`reefapp.stockSolutions.unitPreference`) seeds the default and is
 * never deleted. Defaults to `'gal'`.
 */
import { useCallback, useMemo, useState } from 'react';
import type { VolumeUnit } from '@reefapp/shared';
import { UNIT_PREFERENCE_KEY } from '@reefapp/shared';

import { useServerBackedState } from '../hooks/useServerBackedState';

/** `/api/userdata` namespace holding the unit preference on the Pi. */
export const UNIT_PREFERENCE_NAMESPACE = 'unit-preference';

function readLegacyUnitPreference(): VolumeUnit {
  try {
    const raw = window.localStorage.getItem(UNIT_PREFERENCE_KEY);
    if (raw === 'L' || raw === 'gal') return raw;
    return 'gal';
  } catch {
    return 'gal';
  }
}

export interface UseUnitPreferenceResult {
  unit: VolumeUnit;
  setUnit(u: VolumeUnit): void;
  /** `true` until the first Pi round-trip settles (server or fallback). */
  loading: boolean;
}

export function useUnitPreference(): UseUnitPreferenceResult {
  const [legacyDefault] = useState(readLegacyUnitPreference);
  const { value, setValue, loading } = useServerBackedState<VolumeUnit>(
    UNIT_PREFERENCE_NAMESPACE,
    legacyDefault,
  );
  // Tolerate a malformed server blob — anything but 'L' means gallons.
  const unit: VolumeUnit = value === 'L' ? 'L' : 'gal';

  const setUnit = useCallback(
    (u: VolumeUnit) => {
      setValue(u);
    },
    [setValue],
  );

  return useMemo(() => ({ unit, setUnit, loading }), [unit, setUnit, loading]);
}
